import { useEffect, useMemo, useState } from "react"
import { Card, Button, Space, Select } from "antd"
import { Toast } from "@/components/Toast"
import { PlusOutlined, DeleteOutlined, FilterOutlined } from "@ant-design/icons"
import TrainingProjectTable from "./components/TrainingProjectTable"
import TrainingProjectModal from "./components/TrainingProjectModal"
import { useCrud } from "@/hooks/useCrud"
import { TrainingProject, TrainingProjectFormData } from "./types"
import trainingProjectApi from "./api"
import domainLv2Api from "../domain-lv2/api"
import { DomainLv2 } from "../domain-lv2/types"
import { ID } from "@/types/common"
import { useDomainLv1 } from "@/hooks/useDomainLv1"

const { Option } = Select

const TrainingProjectManagement = () => {
  // 使用全局一级领域数据
  const { domainLv1s, isLoading: domainLv1sLoading } = useDomainLv1()

  // 选中的一级领域id
  const [selectedDomainLv1Id, setSelectedDomainLv1Id] = useState<
    ID | undefined
  >(undefined)
  // 选中的二级领域id
  const [selectedSubdomainId, setSelectedSubdomainId] = useState<
    ID | undefined
  >(undefined)

  // 二级领域列表
  const [subdomains, setSubdomains] = useState<DomainLv2[]>([])

  // 二级领域列表加载状态
  const [subdomainsLoading, setSubdomainsLoading] = useState(false)

  // 默认选中第一个一级领域
  useEffect(() => {
    if (domainLv1s.length > 0 && !selectedDomainLv1Id) {
      setSelectedDomainLv1Id(domainLv1s[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainLv1s])

  // 当一级领域选择变化时，加载对应的二级领域
  useEffect(() => {
    const loadSubdomains = async () => {
      if (selectedDomainLv1Id) {
        try {
          setSubdomainsLoading(true)
          const res = await domainLv2Api.getList(selectedDomainLv1Id)
          const data = res.data || []
          setSubdomains(data)
          // 默认选中第一个二级领域
          if (data.length > 0 && !selectedSubdomainId) {
            const firstDomainLv2Id = data[0].id
            setSelectedSubdomainId(firstDomainLv2Id)
          } else if (data.length === 0) {
            setSelectedSubdomainId(undefined)
          }
        } catch (error) {
          Toast.error("加载二级领域列表失败")
          console.error("加载二级领域列表失败:", error)
        } finally {
          setSubdomainsLoading(false)
        }
      } else {
        setSubdomains([])
        setSelectedSubdomainId(undefined)
      }
    }
    loadSubdomains()
  }, [selectedDomainLv1Id])

  // 使用 useMemo 根据选中的二级领域动态创建 api 对象
  const api = useMemo(
    () => ({
      getList: selectedSubdomainId
        ? () => trainingProjectApi.getList(selectedSubdomainId)
        : () =>
            Promise.resolve({
              data: [],
              total: 0,
              page: 1,
              pageSize: 0,
              totalPages: 0,
            }),
      create: trainingProjectApi.create,
      update: (
        id: ID,
        data: TrainingProjectFormData | Partial<TrainingProjectFormData>,
      ) => trainingProjectApi.update(id, data as TrainingProjectFormData),
      delete: trainingProjectApi.delete,
    }),
    [selectedSubdomainId],
  )

  const {
    items: trainingProjects,
    loading,
    opLoading,
    isModalOpen,
    editingItem: editingTrainingProject,
    selectedIds,
    setSelectedIds,
    handleAdd,
    handleEdit,
    handleSave,
    handleDelete,
    handleBatchDelete,
    handleCloseModal,
    loadItems,
  } = useCrud<TrainingProject, TrainingProjectFormData>({
    api,
    entityName: "训练项目",
    autoLoad: false, // 禁用自动加载，等二级领域默认值设置后再加载
  })

  // 当选中的二级领域变化时，加载对应的训练项目数据
  useEffect(() => {
    if (selectedSubdomainId && loadItems) {
      loadItems() // 使用最新的 api 对象加载数据
    }
  }, [selectedSubdomainId, loadItems])

  // 处理一级领域选择变化
  const handleDomainLv1Change = (domainLv1Id: ID) => {
    setSelectedDomainLv1Id(domainLv1Id)
    setSelectedSubdomainId(undefined) // 清空二级领域选择
    setSelectedIds([]) // 清空选中项
  }

  // 处理二级领域选择变化
  const handleDomainLv2Change = (domainLv2Id: ID) => {
    setSelectedSubdomainId(domainLv2Id)
    setSelectedIds([]) // 清空选中项
  }

  // 计算统计数据
  const selectedCount = selectedIds.length
  const batchDisabled = selectedCount === 0
  // 按钮禁用：必须同时选中一级领域和二级领域
  const addDisabled = !selectedDomainLv1Id || !selectedSubdomainId

  return (
    <div className="p-6">
      {/* 主要内容区域 */}
      <Card>
        {/* 操作栏 */}
        <div className="flex justify-between items-center mb-4">
          <Space>
            <div className="flex items-center gap-2">
              <FilterOutlined className="text-gray-500" />
              <span className="text-sm text-gray-600">一级领域：</span>
              <Select
                placeholder="请选择一级领域"
                style={{ width: 200 }}
                value={selectedDomainLv1Id}
                onChange={handleDomainLv1Change}
                loading={domainLv1sLoading}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    ?.includes(input.toLowerCase())
                }
              >
                {domainLv1s.map((domain) => (
                  <Option key={domain.id} value={domain.id}>
                    {domain.name}
                  </Option>
                ))}
              </Select>
              <span className="text-sm text-gray-600">二级领域：</span>
              <Select
                placeholder="请先选择一级领域"
                style={{ width: 200 }}
                value={selectedSubdomainId}
                onChange={handleDomainLv2Change}
                loading={subdomainsLoading}
                disabled={!selectedDomainLv1Id || subdomains.length === 0}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    ?.includes(input.toLowerCase())
                }
              >
                {subdomains.map((subdomain) => (
                  <Option key={subdomain.id} value={subdomain.id}>
                    {subdomain.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Space>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              loading={opLoading}
              disabled={addDisabled}
              data-testid="btn-add-training-project"
            >
              新增训练项目
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={batchDisabled}
              loading={opLoading}
              data-testid="btn-batch-delete"
            >
              批量删除 {selectedCount > 0 && `(${selectedCount})`}
            </Button>
          </Space>
        </div>

        {/* 训练项目表格 */}
        <TrainingProjectTable
          trainingProjects={trainingProjects}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </Card>

      {/* 训练项目信息模态框 */}
      <TrainingProjectModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        trainingProject={editingTrainingProject}
        loading={opLoading}
        subdomains={subdomains}
        initialValues={{
          domainId: selectedDomainLv1Id,
          domainLv2Id: selectedSubdomainId,
        }}
      />
    </div>
  )
}

export default TrainingProjectManagement
